import React, { useRef, useState } from "react";
import {
  ArrowRight,
  Lightbulb,
  Package,
  NotebookPen,
  ImagePlus,
  CloudUpload,
  Plus,
  Star,
  ShieldCheck,
  Leaf,
  Send,
  X,
} from "lucide-react";
import { IMAGES } from "../mock";
import { FeaturesBar } from "../components/Footer";
import PaintEdges from "../components/PaintEdges";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const themes = {
  // COSPLAY: Blau-Grün
  cosplay: {
    script: "Deine Idee. Unsere Umsetzung.",
    scriptColor: "text-[#6fbf3f]",
    titleA: "COSPLAY-",
    titleB: "ANFRAGE",
    titleAColor: "text-[#1b7fc1]",
    titleBColor: "text-[#3f8f2a]",
    intro:
      "Du hast eine Idee für dein nächstes Cosplay oder suchst nach einem speziellen Accessoire, Rüstungsteil oder Requisite? Dann bist du hier genau richtig!",
    cta: "Cosplay-Anfrage stellen",
    heroImg: IMAGES.cosplaySword,
    cardImgA: IMAGES.cosplayArmor,
    cardImgB: IMAGES.printerClose,
    formTitleA: "COSPLAY-",
    formTitleB: "ANFRAGE",
    btnCls: "btn-gradient",
    iconGrad: "from-[#1b9ad6] to-[#7cc24a]",
    accentText: "text-[#6fbf3f]",
    perkIcon: "text-[#1b9ad6]",
    scriptAccent: "text-[#1b7fc1]",
    dropBorder: "border-sky-300",
    dropBg: "bg-sky-50/50 hover:bg-sky-50",
    dropIcon: "text-[#1b9ad6]",
    dropLabel: "text-[#1b7fc1]",
    heroBg: "from-[#e0f3fc] via-[#f2fbec] to-white",
    glow: "from-sky-300/60 via-white to-lime-300/60",
    paint: { from: "#1b9ad6", via: "#3ec6c0", to: "#7cc24a" },
    bubble: "Deine Idee. Dein Cosplay. Wir machen's möglich!",
  },
  // 3D-DRUCK: Blau-Orange (komplett OHNE Grün)
  druck: {
    script: "Deine Idee. Unsere Umsetzung.",
    scriptColor: "text-[#f7941e]",
    titleA: "3D-DRUCK",
    titleB: "AUF ANFRAGE.",
    titleAColor: "text-[#1b7fc1]",
    titleBColor: "text-[#f7941e]",
    intro:
      "Du hast eine spezielle Idee oder brauchst etwas Bestimmtes? Wir setzen deine Vorstellungen in hochwertige 3D-Drucke um – individuell, präzise und zuverlässig.",
    cta: "3D-Druck Anfrage stellen",
    heroImg: IMAGES.heroPrinter,
    cardImgA: IMAGES.cosplayArmor,
    cardImgB: IMAGES.dragon,
    formTitleA: "3D-DRUCK",
    formTitleB: "ANFRAGE",
    btnCls: "btn-gradient-fire",
    iconGrad: "from-[#1b9ad6] to-[#f7941e]",
    accentText: "text-[#f7941e]",
    perkIcon: "text-[#f7941e]",
    scriptAccent: "text-[#f7941e]",
    dropBorder: "border-orange-300",
    dropBg: "bg-orange-50/50 hover:bg-orange-50",
    dropIcon: "text-[#f7941e]",
    dropLabel: "text-[#e07b12]",
    heroBg: "from-[#e0f3fc] via-[#fff3e2] to-white",
    glow: "from-sky-300/60 via-white to-amber-300/60",
    paint: { from: "#1b9ad6", via: "#7ab5e0", to: "#f7941e" },
    bubble: "Deine Idee. Dein 3D-Druck. Wir machen's möglich!",
  },
};

const Anfrage = ({ type }) => {
  const t = themes[type];
  const formRef = useRef(null);
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [images, setImages] = useState(Array(8).fill(null));
  const [sending, setSending] = useState(false);

  const perks = [
    { icon: Star, title: "Individuelle Anfertigungen", sub: "Statt Massenware", color: t.perkIcon },
    { icon: ShieldCheck, title: "Hochwertige Materialien", sub: "Für lange Haltbarkeit", color: type === "druck" ? "text-[#1b9ad6]" : "text-[#3f8f2a]" },
    { icon: Leaf, title: "Mit Liebe zum Detail", sub: "Dein Wunsch. Unsere Mission.", color: type === "druck" ? "text-[#f7941e]" : "text-[#6fbf3f]" },
  ];

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const onFiles = (files) => {
    const valid = [...files].filter((f) => /image\/(jpeg|png)/.test(f.type) && f.size <= 10 * 1024 * 1024);
    if (valid.length < files.length) {
      toast.error("Nur JPG/PNG bis max. 10 MB pro Bild erlaubt.");
    }
    setImages((prev) => {
      const next = [...prev];
      let slot = next.findIndex((i) => i === null);
      for (const f of valid) {
        if (slot === -1) break;
        next[slot] = { url: URL.createObjectURL(f), name: f.name };
        slot = next.findIndex((i) => i === null, slot + 1);
      }
      return next;
    });
  };

  const removeImage = (idx) =>
    setImages((prev) => prev.map((img, i) => (i === idx ? null : img)));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Bitte fülle alle Pflichtfelder (*) aus.");
      return;
    }
    setSending(true);
    const numImages = images.filter(Boolean).length;
    axios
      .post(`${API}/requests`, {
        type,
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        num_images: numImages,
      })
      .then((res) => {
        setSending(false);
        setForm({ name: "", email: "", phone: "", message: "" });
        setImages(Array(8).fill(null));
        toast.success("Deine Anfrage wurde abgeschickt!", {
          description: res.data?.email_sent
            ? "Wir haben deine Anfrage per E-Mail erhalten und melden uns schnellstmöglich."
            : "Wir haben deine Anfrage gespeichert und melden uns schnellstmöglich.",
        });
      })
      .catch(() => {
        setSending(false);
        toast.error("Senden fehlgeschlagen. Bitte versuche es später erneut.");
      });
  };

  return (
    <main>
      {/* Hero */}
      <section className={`relative overflow-hidden bg-gradient-to-b ${t.heroBg}`}>
        <PaintEdges {...t.paint} />
        <div className="relative mx-auto max-w-[1440px] px-4 sm:px-8 py-12 lg:py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className={`fade-up font-script text-2xl sm:text-3xl ${t.scriptColor}`}>{t.script}</p>
            <h1 className="fade-up-1 mt-2 font-display font-extrabold leading-[1.02] text-[clamp(2.4rem,5.5vw,4.2rem)] tracking-tight">
              <span className={t.titleAColor}>{t.titleA}</span>
              <br />
              <span className={t.titleBColor}>{t.titleB}</span>
            </h1>
            <p className="fade-up-2 mt-5 max-w-xl text-lg text-slate-600 leading-relaxed">{t.intro}</p>
            <div className="fade-up-3 mt-7">
              <button onClick={scrollToForm} className={t.btnCls}>
                {t.cta} <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="relative fade-up-2">
            <div className={`absolute -inset-4 rounded-[2.5rem] blur-xl bg-gradient-to-tr ${t.glow}`} />
            <img
              src={t.heroImg}
              alt={`${t.formTitleA} ${t.formTitleB}`}
              className="relative rounded-[2rem] shadow-2xl w-full h-[300px] sm:h-[400px] object-cover"
            />
            <div className="absolute top-4 right-4 rounded-2xl bg-white/85 backdrop-blur px-4 py-2 shadow-lg max-w-[240px]">
              <p className={`font-script text-xl leading-tight ${t.scriptAccent}`}>{t.bubble}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Info-Karten */}
      <section className="mx-auto max-w-[1440px] px-4 sm:px-8 py-10 grid md:grid-cols-2 gap-8">
        <article className="card-soft flex overflow-hidden">
          <div className="flex-1 p-7">
            <div className="flex items-center gap-4">
              <div className={`h-14 w-14 rounded-full bg-gradient-to-br ${t.iconGrad} flex items-center justify-center shrink-0`}>
                <Lightbulb className="h-7 w-7 text-white" />
              </div>
              <h3 className="font-display text-2xl font-extrabold">
                <span className="text-[#1b7fc1]">DEINE</span>{" "}
                <span className={t.accentText}>IDEE</span>
              </h3>
            </div>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Ob Rüstung, Waffe, Accessoire oder komplettes Kostüm – erzähle uns von deinem Wunsch.
            </p>
            <button onClick={scrollToForm} className={`${t.btnCls} mt-5`}>
              Anfrage starten <ArrowRight className="h-5 w-5" />
            </button>
          </div>
          <img src={t.cardImgA} alt="Deine Idee" className="hidden sm:block w-40 lg:w-48 object-cover" />
        </article>

        <article className="card-soft flex overflow-hidden">
          <div className="flex-1 p-7">
            <div className="flex items-center gap-4">
              <div className={`h-14 w-14 rounded-full bg-gradient-to-br ${t.iconGrad} flex items-center justify-center shrink-0`}>
                <Package className="h-7 w-7 text-white" />
              </div>
              <h3 className="font-display text-2xl font-extrabold leading-tight">
                <span className="text-[#1b7fc1]">INDIVIDUELLE</span><br />
                <span className={t.accentText}>UMSETZUNG</span>
              </h3>
            </div>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Wir prüfen die Machbarkeit, beraten dich zu Materialien, Details und Design.
            </p>
            <button onClick={scrollToForm} className={`${t.btnCls} mt-5`}>
              Beratung erhalten <ArrowRight className="h-5 w-5" />
            </button>
          </div>
          <img src={t.cardImgB} alt="Individuelle Umsetzung" className="hidden sm:block w-40 lg:w-48 object-cover" />
        </article>
      </section>

      {/* Formular */}
      <section ref={formRef} className="mx-auto max-w-[1440px] px-4 sm:px-8 py-6 scroll-mt-24">
        <div className="card-soft p-6 sm:p-10 grid lg:grid-cols-[1fr_320px] gap-10">
          <div>
            <div className="flex items-center gap-4">
              <div className={`h-14 w-14 rounded-full bg-gradient-to-br ${t.iconGrad} flex items-center justify-center shrink-0`}>
                <NotebookPen className="h-7 w-7 text-white" />
              </div>
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold">
                  <span className="text-[#1b7fc1]">{t.formTitleA}</span>{" "}
                  <span className={t.accentText}>{t.formTitleB}</span>
                </h2>
                <p className="text-slate-500 mt-1">
                  Beschreibe uns einfach, was du dir vorstellst. Je mehr Details, desto besser
                  können wir dir ein Angebot machen.
                </p>
              </div>
            </div>

            <form onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-5">
              <input
                className="input-soft"
                placeholder="Name / Nickname *"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <input
                className="input-soft"
                type="email"
                placeholder="E-Mail-Adresse *"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <input
                className="input-soft sm:col-span-2"
                placeholder="Telefon (optional)"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
              <textarea
                className="input-soft sm:col-span-2 min-h-[150px] resize-y"
                placeholder="Deine Anfrage / Beschreibung * – Erzähle uns von deinem Wunsch (z. B. Kostüm, Rüstung, Waffe, Accessoire, Requisite, Material, Größe, Bilder ...)"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
              <div className="sm:col-span-2">
                <button type="submit" disabled={sending} className={`${t.btnCls} w-full sm:w-auto disabled:opacity-60`}>
                  <Send className="h-5 w-5" />
                  {sending ? "Wird gesendet ..." : "Anfrage abschicken"}
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </form>
          </div>

          <aside className="lg:border-l lg:border-slate-100 lg:pl-10 flex flex-col justify-center gap-8">
            {perks.map((p) => (
              <div key={p.title} className="flex items-center gap-4">
                <p.icon className={`h-11 w-11 ${p.color} shrink-0`} strokeWidth={1.6} />
                <div>
                  <p className="font-extrabold text-[#1b7fc1] font-display">{p.title}</p>
                  <p className="text-sm text-slate-500">{p.sub}</p>
                </div>
              </div>
            ))}
          </aside>
        </div>
      </section>

      {/* Bilder einfügen */}
      <section className="mx-auto max-w-[1440px] px-4 sm:px-8 py-6 pb-14">
        <div className="card-soft p-6 sm:p-10">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className={`h-14 w-14 rounded-full bg-gradient-to-br ${t.iconGrad} flex items-center justify-center shrink-0`}>
                <ImagePlus className="h-7 w-7 text-white" />
              </div>
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold">
                  <span className="text-[#1b7fc1]">BILDER</span>{" "}
                  <span className={t.accentText}>EINFÜGEN</span>
                </h2>
                <p className="text-slate-500 mt-1 max-w-xl">
                  Lade hier gerne Bilder deiner Ideen, Vorlagen oder Referenzen hoch. So können wir
                  uns ein besseres Bild von deinem Wunsch machen und dir schneller und gezielter helfen.
                </p>
              </div>
            </div>
            <p className={`font-script text-2xl rotate-[-2deg] ${t.scriptAccent}`}>
              Aus Ideen werden echte Unikate
            </p>
          </div>

          <div className="mt-8 grid lg:grid-cols-2 gap-6">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className={`rounded-3xl border-2 border-dashed ${t.dropBorder} ${t.dropBg} transition-colors duration-300 flex flex-col items-center justify-center gap-3 py-14 px-6 text-center`}
            >
              <CloudUpload className={`h-12 w-12 ${t.dropIcon}`} strokeWidth={1.5} />
              <span className={`font-bold text-lg ${t.dropLabel}`}>
                Bilder hier hochladen oder per Klick auswählen
              </span>
              <span className="text-sm text-slate-500">JPG, PNG (max. 10 MB pro Bild)</span>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png"
              multiple
              hidden
              onChange={(e) => onFiles(e.target.files || [])}
            />

            <div>
              <p className="text-sm font-bold text-slate-500 mb-3">Deine Bilder (optional)</p>
              <div className="grid grid-cols-4 gap-3">
                {images.map((img, i) => (
                  <div
                    key={`bild-slot-${i}`}
                    className={`relative aspect-square rounded-2xl border-2 border-dashed ${t.dropBorder} bg-slate-50/60 flex flex-col items-center justify-center overflow-hidden group`}
                  >
                    {img ? (
                      <>
                        <img src={img.url} alt={`Bild ${i + 1}`} className="absolute inset-0 h-full w-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImage(i)}
                          className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/60 text-white items-center justify-center hidden group-hover:flex"
                          aria-label="Bild entfernen"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        className="flex flex-col items-center gap-1 text-slate-400 hover:text-[#1b9ad6] transition-colors"
                      >
                        <Plus className="h-6 w-6" />
                        <span className="text-xs font-semibold">Bild {i + 1}</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FeaturesBar />
    </main>
  );
};

export default Anfrage;
